import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonGroup, Card, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useState } from 'react';
import logo from '../../../assets/logo_no_text_primary.png';

function ApproveVendorCardComponent({ eventApplicationInfo, handleSubmit, eventInfo }) {

    const navigate = useNavigate();

    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedVendorSlug, setSelectedVendorSlug] = useState(null);
    const [comment, setComment] = useState("");

    const handleOpenDialog = (status, vendorSlug) => {
        setSelectedStatus(status);
        setSelectedVendorSlug(vendorSlug);
        setComment("");
        setCommentDialogOpen(true);
    };

    const handleDialogSubmit = () => {
        handleSubmit(selectedStatus, selectedVendorSlug, comment);
        setCommentDialogOpen(false);
    };

    const VendorApplicationDialog = () => {
        return (
            <Dialog
                open={vendorDialogOpen}
                onClose={() => setVendorDialogOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        //   padding: 1,
                    },
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                    Vendor Application Summary
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            📍 {eventApplicationInfo?.storefront_name || 'Vendor'}
                        </Typography>
                        <Typography variant="body1">
                            🌟 <strong>Type:</strong> {eventApplicationInfo?.storefront_cuisine || 'N/A'}
                        </Typography>
                        <Typography variant="body1">
                            🏕️ <strong>Booth Name:</strong> {eventApplicationInfo?.booth_type_name || 'N/A'} (${eventApplicationInfo?.booth_type_cost || '0'})
                        </Typography>
                        <Typography variant="body1">
                            🕓 <strong>Submitted:</strong>{' '}
                            {eventApplicationInfo?.submitted_at
                                ? new Date(eventApplicationInfo.submitted_at).toLocaleString('en-US', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: 'numeric',
                                    hour12: true
                                })
                                : 'N/A'}
                        </Typography>
                        <Typography variant="body1">
                            📌 <strong>Status:</strong> {eventApplicationInfo?.application_status || 'N/A'}
                        </Typography>
                    </Box>
                    {eventApplicationInfo?.permit &&
                        <Button
                            endIcon={<OpenInNewIcon />}
                            sx={{
                                mt: 2,
                                p: 0,
                                borderRadius: 3,
                                fontWeight: 'bold',
                                fontSize: '1rem', // equivalent to body1
                                textTransform: 'none' // keeps the casing as-is
                            }}
                            onClick={() => window.open(eventApplicationInfo?.permit, '_blank')}
                        >
                            👁️ View Permit
                        </Button>
                    }
                    <br/>
                    <a
                        href={`/${eventApplicationInfo?.storefront_slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <Button
                            variant="text"
                            color="primary"
                            startIcon={<OpenInNewIcon />}
                            sx={{ mt: 1, borderRadius: 3, ml: -0.7 }}
                        >
                            View Vendor Profile
                        </Button>
                    </a>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-end', paddingX: 3, paddingY: 2 }}>
                    <Button
                        sx={{ borderRadius: 4, px: 3 }}
                        onClick={() => setVendorDialogOpen(false)}
                        variant="outlined"
                        color="secondary"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Card sx={{ p: 1, borderRadius: 3 }} >
            <Grid container
                alignItems="center"
                textAlign={'center'}
                spacing={1}>

                {/* Left Section: Image */}
                <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
                    <Box sx={{ width: '100%', maxWidth: '5rem' }}> {/* Dynamic size */}
                        <img src={eventApplicationInfo?.storefront_logo || logo} alt="Sidewalk Logo" style={{ width: '100%', height: 'auto', borderRadius: '50%', objectFit: 'cover' }} />
                    </Box>
                </Grid>

                {/* Right Section: Text Content */}
                <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {eventApplicationInfo?.storefront_name || 'N/A'}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <ButtonGroup variant="text" aria-label="Basic button group">
                        <Button
                            onClick={() => handleOpenDialog("accepted", eventApplicationInfo?.storefront_slug)}
                            disabled={eventApplicationInfo?.application_status !== "pending"}
                        >✅</Button>
                        <Button
                            onClick={() => handleOpenDialog("rejected", eventApplicationInfo?.storefront_slug)}
                            disabled={eventApplicationInfo?.application_status !== "pending"}
                        >❌</Button>
                        <Button
                            onClick={() => setVendorDialogOpen(true)}
                        >
                            <VisibilityIcon sx={{ color: 'steelblue' }} />
                        </Button>
                    </ButtonGroup>
                </Grid>
            </Grid>
            <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} fullWidth>
                <DialogTitle>Add Optional Comment</DialogTitle>
                <DialogContent>
                    {eventInfo?.booth_types?.some(
                      booth => booth.name === eventApplicationInfo?.booth_type_name &&
                               booth.booths_sold + 1 > booth.quantity_available
                    ) && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        All <strong>{eventApplicationInfo?.booth_type_name}</strong> booths are sold out. Approving this will exceed the limit.
                      </Alert>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Comment"
                        fullWidth
                        variant="outlined"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 4,
                            }
                        }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
                    <Button sx={{ borderRadius: 3 }}
                        disableElevation
                        onClick={handleDialogSubmit} variant="contained">Submit</Button>
                </DialogActions>
            </Dialog>
            <VendorApplicationDialog />
        </Card>
    )
}

export default ApproveVendorCardComponent